// Display · Hairline
//
// Canonical thin horizontal divider. Migrated from `src/components/ui/Hairline.tsx`.
//
// Atom — no events, no configurable affordances. The Output and Actions
// interfaces are dropped per the @ds atom convention (Rule 3 / ds-engineer.md).
//
// All visual values come from `@ds/tokens/palette`.

import { SURFACE } from "@ds/tokens/palette";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface HairlineInput {
  /**
   * Variant controls which border token is used.
   * - `"default"` (default) — `SURFACE.border` (#e8eaed). Use for section frames and card dividers.
   * - `"soft"` — `SURFACE.borderSoft` (#eef0f2). Use for between-row hairlines inside dense grids.
   */
  variant?: "default" | "soft";
  /**
   * Vertical margin shorthand (applied as `marginBlock`).
   * Accepts any `SPACE.*` token value. Default `0`.
   */
  spacing?: number;
  /** `data-testid` on the root `<hr>`. Atoms do NOT auto-emit inner testids. */
  testId?: string;
}

// ─── Component contract ──────────────────────────────────────────────────
//
// Atom: no Output (no events emitted), no Actions (no configurable
// affordances). The empty interfaces are dropped per @ds convention.

export type HairlineProps = HairlineInput;

// ─── Implementation ──────────────────────────────────────────────────────

export function Hairline({
  variant = "default",
  spacing = 0,
  testId,
}: HairlineProps) {
  const borderColor = variant === "soft" ? SURFACE.borderSoft : SURFACE.border;

  return (
    <hr
      data-testid={testId}
      style={{
        margin:      0,
        marginBlock: spacing,
        border:      0,
        borderTop:   `1px solid ${borderColor}`,
      }}
    />
  );
}
